// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SectionWrapper from "@adl/App/Components/Common/SectionWrapper";
import { getNotifyStyle } from "@adl/App/Components/Notify";
import InvoiceSummaryCard, { InvoiceSummaryCardStyles } from "@adl/App/Widgets/OpenInvoices/InvoiceSummaryCard";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadInvoices from "@insite/client-framework/Store/Data/Invoices/Handlers/LoadInvoices";
import { getInvoicesDataView } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    const userRoles = isAuthenticated ? session.userRoles.trim().split(",") : [];
    const hideOpenInvoicesView = userRoles.length === 1 && userRoles.includes("Buyer1");
    const userRolesUntrimmed = isAuthenticated ? session.userRoles  : "";
    const isInvoiceDisabledUser = userRolesUntrimmed.includes("BuyerOnly");
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        invoicesDataView: getInvoicesDataView(state, openInvoicesParameter),
        myInvoicePageNavLink: getPageLinkByPageType(state, "InvoiceHistoryPage"),
        canViewInvoices: isAuthenticated,
        hideOpenInvoicesView,
        isInvoiceDisabledUser,
    };
};

const mapDispatchToProps = {
    loadInvoices,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OpenInvoicesStyles {
    cardList?: CardListStyles;
    noInvoicesGridItem?: GridItemProps;
    noInvoicesText?: TypographyPresentationProps;
    invoiceSummaryCard?: InvoiceSummaryCardStyles;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
}

export const recentQuotesStyles: OpenInvoicesStyles = {
    noInvoicesGridItem: { width: 12 },
    cardList: {
        gridContainer: {
            gap: 0,
        },
    },
    noInvoicesText: {
        ...getNotifyStyle("info"),
    },
};

const openInvoicesParameter = {
    page: 1,
    pageSize: 5,
    customerSequence: "-1",
    showOpenOnly: true,
};

const styles = recentQuotesStyles;

class OpenInvoices extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewInvoices && !this.props.invoicesDataView.value && !this.props.isInvoiceDisabledUser) {
            this.props.loadInvoices(openInvoicesParameter);
        }
    }

    render() {
        const {
            settingsCollection,
            session,
            invoicesDataView,
            myInvoicePageNavLink,
            canViewInvoices,
            hideOpenInvoicesView,
            isInvoiceDisabledUser,
        } = this.props;
        
        if (!canViewInvoices || !invoicesDataView.value || isInvoiceDisabledUser) {
            return null;
        }

        const myInvoicePageUrl = myInvoicePageNavLink ? myInvoicePageNavLink.url : undefined;

        // XNG-Changes: change structure to Table
        // XNG-Changes: hide DataView for users who only has `Buyer1` Role
        if (invoicesDataView.value.length === 0 || hideOpenInvoicesView) {
            return null;
        }

        return (
            <SectionWrapper
                isAccordion={true}
                title={translate("Open Invoices")}
                link={myInvoicePageUrl}
                linkText={translate("View All")}
            >
                {invoicesDataView.value.length > 0 && (
                    <GridItem width={12}>
                        <DataTable {...styles.dataTable} className="mobile-view">
                            <DataTableHead {...styles.dataTableHead}>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Date")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Invoice #")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("PO #")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Job Name")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>
                                    {translate("Ship To / Pick Up")}
                                </DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>{translate("Due Date")}</DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>
                                    {translate("Total Amount")}
                                </DataTableHeader>
                                <DataTableHeader {...styles.dataTableHeaders}>
                                    {translate("Current Balance")}
                                </DataTableHeader>
                            </DataTableHead>
                            <DataTableBody {...styles.dataTableBody}>
                                {invoicesDataView.value.map(
                                    invoice =>
                                        invoice !== null && (
                                            <DataTableRow
                                                {...styles.dataTableRow}
                                                data-test-selector="openInvoices_dataLine"
                                                key={invoice.id}
                                            >
                                                <InvoiceSummaryCard
                                                    invoice={invoice}
                                                    session={session}
                                                    invoiceSettings={settingsCollection.invoiceSettings}
                                                    extendedStyles={styles.invoiceSummaryCard}
                                                />
                                            </DataTableRow>
                                        ),
                                )}
                            </DataTableBody>
                        </DataTable>
                    </GridItem>
                )}
            </SectionWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OpenInvoices),
    definition: {
        group: "Common",
        icon: "list-ul",
    },
};

export default widgetModule;
